"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  ScrollText,
  Sparkles,
  Building2,
  Crown,
  Vote,
  Briefcase,
  ShieldCheck,
  Lock,
  Eye,
  Radio,
  Activity,
  Target,
  Users,
  Award,
  ArrowRight,
  Mic,
  Compass,
  Zap,
  AlertTriangle,
  Globe2,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { BentoBlock } from "@/lib/blocks/bento-block";
import { CounterBlock } from "@/lib/blocks/counter-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const HEADLINE_STATS = [
  { metric: "15", suffix: "/18", label: "Campaign Victories", sub: "Across sovereign engagements", accent: "amber-gold" },
  { metric: "0", suffix: "", label: "Security Breaches", sub: "Fifteen-plus years", accent: "red" },
  { metric: "99.9999", suffix: "%", label: "Uptime", sub: "31.5 sec max/year", accent: "cyan" },
  { metric: "89", suffix: "%", label: "Prediction Accuracy", sub: "Crisis forecasting", accent: "purple" },
  { metric: "300", suffix: "+", label: "Elite Clients", sub: "Eight distinct sectors", accent: "emerald" },
  { metric: "60", suffix: "min", label: "Decision Time", sub: "From 24-72 hours", accent: "denim" },
];

const CHALLENGES = [
  {
    icon: Lock,
    title: "Secure Executive Communications",
    body: "Presidential and prime ministerial offices generate communications of the highest sensitivity — policy directives, defense posture decisions, diplomatic negotiations, cabinet deliberations. A single intercepted communication can compromise national security, derail diplomatic relationships, or hand adversaries the strategic advantage. The encryption standard required is sovereign-grade — quantum-resistant, metadata-free, architecturally impossible to intercept.",
    accent: "amber-gold",
  },
  {
    icon: Eye,
    title: "Real-Time Threat Intelligence",
    body: "Executive offices face threats across every domain — cyber attacks, coordinated disinformation campaigns, foreign intelligence operations, insider threats, economic sabotage, reputational warfare. Intelligence must arrive in under sixty seconds, carry actionable specificity, and integrate across all threat domains into a unified operational picture.",
    accent: "cyan",
  },
  {
    icon: Radio,
    title: "National Perception Management",
    body: "The public narrative surrounding an executive office is a strategic asset or vulnerability. Unmanaged perception becomes opposition-controlled narrative. Opposition-controlled narrative becomes public distrust. Public distrust becomes political instability. Managing national perception at the executive level requires real-time sentiment analysis, predictive narrative modeling, coordinated media strategy, and the capacity to deploy counter-narratives across every digital platform.",
    accent: "purple",
  },
  {
    icon: Activity,
    title: "Inter-Agency Coordination",
    body: "Executive offices preside over complex ecosystems of ministries, defense agencies, intelligence services, law enforcement bodies, and diplomatic corps. Without unified command, agencies operate in silos, creating intelligence gaps, communication blind spots, and coordination failures that adversaries exploit. The coordination required is not bureaucratic — it is neural, operating at the speed of decisions, not the speed of memos.",
    accent: "emerald",
  },
  {
    icon: ShieldCheck,
    title: "Geopolitical Intelligence",
    body: "Executive offices operate in a multipolar world where diplomatic relationships shift, sanctions regimes evolve, and conflict risks materialize without warning. The intelligence required must span one hundred ninety-five-plus countries, track alliance dynamics in real time, forecast regional stability with calibrated confidence, and provide the contextual understanding that separates informed statecraft from reactive improvisation.",
    accent: "red",
  },
  {
    icon: Target,
    title: "Crisis Transformation at Speed",
    body: "Executive offices face crises that can define a presidency. The Golden Hour Principle: ninety-five percent of crisis damage occurs in the first six hours. Crisis Response Engine compresses that critical damage window by ninety-nine point nine percent — transforming crises from existential threats into opportunities for narrative reinforcement.",
    accent: "denim",
  },
];

const PILLARS = [
  { icon: Eye, title: "Perception, Reputation & Public Relations", body: "National perception management. Real-time sentiment analysis across two hundred-plus platforms and one hundred ninety-five-plus countries. Predictive narrative modeling with calibrated confidence intervals. Counter-narrative deployment before misinformation crystallizes into consensus.", accent: "red" },
  { icon: Lock, title: "Privacy, Encryption & Information Security", body: "Sovereign-grade encryption for executive communications. Quantum-resistant cryptography with post-quantum algorithms. Complete metadata elimination. Air-gapped deployment for classified environments. Zero-knowledge architecture where even the infrastructure operators cannot access content.", accent: "purple" },
  { icon: Radio, title: "Policing, Intelligence & Defense", body: "Real-time threat detection across cyber, physical, informational, and economic domains. Multi-agency intelligence fusion in under five minutes. Counter-intelligence and cyber forensics at sovereign tempo. Geopolitical forecasting with probability-weighted outcomes.", accent: "cyan" },
  { icon: Award, title: "Policy, Governance & Transformation", body: "eGovernance transformation at national scale. Policy intelligence grounded in data, not ideology. Cross-jurisdictional comparison and predictive outcome forecasting. Public scheme implementation tracking with verified delivery. The same architecture that wins elections also governs from them.", accent: "amber-gold" },
];

const PLATFORMS = [
  {
    title: "Security Operations Layer",
    subtitle: "Encrypted Communications",
    body: "Sovereign security platform providing encrypted communications, network hardening, threat intelligence, and cyber forensics. Seven independent security layers. Quantum-resistant encryption with CRYSTALS-Kyber-768 and CRYSTALS-Dilithium3. Ninety-nine point nine nine nine nine percent uptime, zero security breaches across fifteen-plus years.",
    accent: "red",
  },
  {
    title: "Intelligence Operations Layer",
    subtitle: "Operational Intelligence",
    body: "AI-driven digital intelligence providing real-time social listening, big data mining, public sentiment analysis, threat detection, and predictive analytics. Monitors two hundred-plus platforms, one hundred thousand-plus news sources, one thousand-plus dark web sources. Five intelligence dimensions covering every tempo at which executive decisions are made.",
    accent: "purple",
  },
  {
    title: "Operations Coordination Layer",
    subtitle: "Cross-Platform Command",
    body: "Neural command interface that unifies all platforms into a single operational fabric. Ninety-five percent coordination success rate. Decision time reduction from twenty-four to seventy-two hours to under one hour. Five-tier command hierarchy with five sensitivity levels. The neural command center that eliminates coordination failure.",
    accent: "cyan",
  },
  {
    title: "Crisis Response Engine",
    subtitle: "Crisis Transformation",
    body: "Rapid-response platform for crisis containment and reputation recovery. Three hundred eighty-four to one thousand four hundred sixteen times the speed of traditional crisis management. Sub-five-minute total response from detection to deployment. Eighty-five to ninety-five percent success rates transforming crises into narrative reinforcement opportunities.",
    accent: "amber-gold",
  },
];

const SERVICES = [
  { title: "Secure Executive Communications", body: "End-to-end encryption for all modalities — voice, text, video, data — with complete metadata elimination. Signal Protocol with post-quantum extensions. Ephemeral messaging with cryptographic proof of deletion. Group communications up to one thousand participants. CryptoRouter appliances providing hardware-accelerated throughput with zero measurable latency.", accent: "red" },
  { title: "Real-Time Threat Intelligence", body: "Ten proprietary modules — OMNI-SURVEILLANCE, PREDICTIVE INTELLIGENCE ENGINE, THREAT DETECTION MATRIX, SENTIMENT TRIANGULATION, CROSS-PLATFORM CORRELATION, NATURAL LANGUAGE QUANTUM ENGINE, VISUAL INTELLIGENCE SUITE, GEO-SPATIAL INTELLIGENCE, COMPETITIVE INTELLIGENCE SUITE, and ACTION AND RESPONSE — processing over five hundred million data points daily.", accent: "cyan" },
  { title: "National Perception Engineering", body: "Narrative engineering across fifty-plus digital platforms with ninety-five to ninety-eight percent organic authenticity. Crisis containment, narrative transformation, and reputation rehabilitation. Real-time sentiment triangulation across eight dimensions — polarity, emotional resonance, intensity, velocity, demographic segmentation, influencer attribution, narrative frame, temporal persistence.", accent: "purple" },
  { title: "Inter-Agency Coordination", body: "Multi-agency intelligence fusion in under five minutes. Five-tier command hierarchy from Supreme Commander to Viewers. Data compartmentalization across five sensitivity levels from Public through Top Secret. The neural coordination that eliminates the spaces between agencies that adversaries exploit.", accent: "emerald" },
  { title: "Geopolitical Intelligence & Forecasting", body: "International relations monitoring across diplomatic developments, bilateral and multilateral relationships, and alliance dynamics. Geopolitical risk assessment covering political stability, regulatory risk, sanctions exposure, and conflict risk. Twenty-four to seventy-two-hour crisis early warning with calibrated confidence intervals.", accent: "amber-gold" },
  { title: "Crisis Transformation & Recovery", body: "Crisis Response Engine deployment for crisis containment. Five hundred-plus pre-built response playbooks across twelve crisis categories. The Transformative option achieving eighty-seven percent success rate by converting crises into narrative reinforcement. Recovery to baseline in thirty to sixty days versus ninety to one hundred eighty traditional.", accent: "denim" },
];

const CLIENTS = [
  { title: "Presidential Administrations", body: "Executive offices at the apex of sovereign power — shaping economies, commanding defense postures, defining national narratives, influencing the lives of millions. Every decision radiates outward.", accent: "amber-gold" },
  { title: "Prime Ministerial Secretariats", body: "Head-of-government offices coordinating complex ecosystems of ministries, agencies, and diplomatic corps. Decision compression from days to under one hour through neural command architecture.", accent: "red" },
  { title: "Executive Branches of Government", body: "Cabinet offices, executive secretariats, and the immediate support infrastructure of heads of state and government requiring absolute discretion and sovereign-grade operational discipline.", accent: "purple" },
  { title: "National Security Councils", body: "Coordination bodies overseeing the full spectrum of national security policy. Multi-source intelligence fusion, inter-agency command architecture, and crisis response at sovereign tempo.", accent: "cyan" },
];

export default function PresidentialPmOfficesPage() {
  return (
    <>
      <PageHero
        icon={ScrollText}
        overline="Presidential & Prime Ministerial Offices"
        overlineClassName="text-amber-gold"
        title={
          <>
            Strategic Command at <span className="text-gradient-premium">the Highest Level.</span>
          </>
        }
        subtitle="Executive offices sit at the apex of sovereign power. Every decision radiates outward — shaping economies, commanding defense postures, defining national narratives. At this altitude, the margin for error is nonexistent. Dr. Jyoti Kush operates precisely at this altitude."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Sectors", href: "/sectors/" }, { label: "Presidential & PM Offices" }]}
        badge="Sovereign-grade command. Verified outcomes."
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
              <Sparkles className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">Executive Summary</span>
            </div>
            <h2 className="text-display-md mb-5">
              The most demanding operational environment in existence.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Presidential and prime ministerial offices represent the most demanding operational environment in existence. The stakes are existential. The timelines are compressed. The adversaries — both foreign and domestic — are sophisticated, well-resourced, and relentless. The information environment is saturated with noise, disinformation, and coordinated narrative attacks designed to destabilize, discredit, and disarm.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Dr. Jyoti Kush brings to this environment an operating method forged through fifteen-plus years of sovereign-scale engagements across eighteen countries on three continents. The approach does not treat capabilities as separate service lines. They are an integrated closed-loop system where intelligence flows from perception into politics, ground-truth data informs policy, policy directs policing, and policing protects privacy.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The organizations that engage this level of operational support are not seeking advice. They are seeking verified outcomes — the kind delivered only by an operator whose integrated architecture, multi-domain teams, and fifteen-plus years of sovereign-scale operations constitute the most comprehensive strategic infrastructure in existence. The record speaks with the authority only verified results can command.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {HEADLINE_STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${ACCENT[s.accent].bg} to-transparent border ${ACCENT[s.accent].border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-sm font-bold ${ACCENT[s.accent].text} leading-none mb-2`}>
                  {s.metric}{s.suffix}
                </div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                <div className="text-caption text-muted-foreground">{s.sub}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <AlertTriangle className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Executive Challenge</span>
          </div>
          <h2 className="text-display-md mb-4">Six challenges that define the highest office.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Executive offices of government operate under conditions no other organizational form endures simultaneously. The challenges are technical, strategic, perceptual, and defensive — addressed as an integrated whole, not as disconnected problem sets.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CHALLENGES.map((c, i) => {
            const a = ACCENT[c.accent];
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl" />
                <div className="relative">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg`}>
                    <c.icon className="size-6 text-white" />
                  </div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{c.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{c.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Zap className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Four-Pillar Activation</span>
          </div>
          <h2 className="text-display-md mb-4">How the framework serves executive offices.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Executive offices activate four of the five pillars of the Penta P Framework — Perception, Privacy, Policing, and Policy — operating as one integrated architecture. Politics is subsumed within the executive mandate.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {PILLARS.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-3.5">
                  <div className={`size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <p.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{p.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <ShieldCheck className="size-3.5 text-red" />
            <span className="text-overline text-red">Key Platforms</span>
          </div>
          <h2 className="text-display-md mb-4">The technology architecture behind sovereign-scale command.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Four core platforms form the operational backbone. Each was built in-house, refined through mission-critical deployment across eighteen countries, and continues to evolve through continuous AI/ML improvement.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {PLATFORMS.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-3.5">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <Lock className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md ${a.text}`}>{p.title}</h3>
                    <span className="text-overline text-muted-foreground">{p.subtitle}</span>
                    <p className="text-body-sm text-muted-foreground leading-relaxed mt-3">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Briefcase className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Service Domains</span>
          </div>
          <h2 className="text-display-md mb-4">Six integrated service domains for executive offices.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Not standalone engagements — coordinated operations where intelligence from one domain strengthens execution in every other. The architecture is the same that serves defense agencies and governments. The standard does not adjust.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{s.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{s.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Users className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Ideal Clients</span>
          </div>
          <h2 className="text-display-md mb-4">Who benefits from this engagement.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Executive offices that measure by outcomes, not by media impressions or political theater. The standard is sovereign. The approach is the same. The discretion is absolute.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {CLIENTS.map((c, i) => {
            const a = ACCENT[c.accent];
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{c.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{c.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <span className="text-overline text-amber-gold">Signature Architecture</span>
          </div>
          <h2 className="text-display-md mb-4">Executive Office Support Bento.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Eight capabilities that compose the integrated support architecture for executive offices. Each capability operates under neural command, coordinated by LITHVIK N1 with 95% coordination success.
          </p>
        </div>
        <BentoBlock
          items={[
            { title: "Sovereign Communications", body: "End-to-end encryption for all modalities with complete metadata elimination at the protocol level. Signal Protocol with post-quantum extensions, ephemeral messaging with cryptographic proof of deletion.", icon: <Lock className="size-5" />, accent: "red", span: "lg" },
            { title: "Threat Intelligence", body: "Sub-60-second alerting across 200+ platforms, 100,000+ news sources, 1,000+ dark web sources in 15+ languages.", icon: <Eye className="size-5" />, accent: "cyan" },
            { title: "Geopolitical Forecasting", body: "195+ countries tracked in real time. 24-72 hour crisis early warning with calibrated confidence intervals.", icon: <Compass className="size-5" />, accent: "purple" },
            { title: "National Perception Engineering", body: "Narrative deployment across 50+ platforms with 95-98% organic authenticity. Counter-narrative orchestration before misinformation crystallizes.", icon: <Radio className="size-5" />, accent: "amber-gold", span: "wide" },
            { title: "Inter-Agency Coordination", body: "Multi-agency intelligence fusion in under 5 minutes. Five-tier command hierarchy with five sensitivity levels from Public through Top Secret.", icon: <Activity className="size-5" />, accent: "emerald" },
            { title: "Crisis Transformation", body: "Sub-5-minute total response from detection to deployment. 384x-1,416x speed advantage with 85-95% success rate.", icon: <Zap className="size-5" />, accent: "red" },
            { title: "Governance Intelligence", body: "129 capabilities across five governance pillars. Policy-to-impact tracking for every government program.", icon: <Briefcase className="size-5" />, accent: "amber-gold" },
            { title: "Diplomatic Intelligence", body: "International relations monitoring across bilateral and multilateral relationships, alliance dynamics, sanctions exposure, and conflict risk.", icon: <Globe2 className="size-5" />, accent: "denim", span: "wide" },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <span className="text-overline text-cyan">Sovereign Standard Metrics</span>
          </div>
          <h2 className="text-display-md mb-4">Verified operational data from sovereign deployments.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The standard by which executive offices measure sovereign-grade capability. These are not aspirational targets — they are verified operational outcomes.
          </p>
        </div>
        <CounterBlock
          columns={3}
          stats={[
            { value: 0, suffix: "", label: "Security Breaches", description: "Across 15+ years of executive office engagements", accent: "red" },
            { value: 99.9999, suffix: "%", label: "Operational Uptime", description: "Maximum 31.5 seconds of downtime per year", accent: "cyan" },
            { value: 89, suffix: "%", label: "Crisis Prediction", description: "72-hour average advance warning", accent: "purple" },
            { value: 95, suffix: "%", label: "Coordination Success", description: "LITHVIK N1 across all concurrent platforms", accent: "emerald" },
            { value: 100, suffix: "%", label: "Client Satisfaction", description: "Verified across 300+ elite clients", accent: "amber-gold" },
            { value: 60, suffix: "min", label: "Decision Time", description: "From 24-72 hours through orchestration", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <span className="text-overline text-red">LITHVIK Methodology</span>
          </div>
          <h2 className="text-display-md mb-4">The five-phase methodology for executive engagements.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every engagement with a presidential or prime ministerial office follows the LITHVIK Methodology — the operating discipline refined across 15+ years of sovereign-scale operations.
          </p>
        </div>
        <FeatureGridBlock
          columns={3}
          features={[
            { title: "1. Discovery", body: "Comprehensive mapping of the executive office's intelligence landscape. Complete threat model, capability gap analysis, and operational blueprint grounded in evidence collected through CLAIRVOYANCE CX.", icon: <Eye className="size-5" />, accent: "amber-gold" },
            { title: "2. Architecture", body: "Operational architecture design specifying which platforms activate, which services deploy, what intelligence streams integrate, and what coordination structures link the office to the broader ecosystem.", icon: <Briefcase className="size-5" />, accent: "cyan" },
            { title: "3. Deployment", body: "Coordinated phased deployment — S3-SENTINEL establishes sovereign communications, CLAIRVOYANCE CX activates monitoring, LITHVIK N1 establishes command hierarchy, PHOENIX-1 positions response playbooks.", icon: <Zap className="size-5" />, accent: "red" },
            { title: "4. Execution", body: "Continuous 24/7/365 operations with daily briefs, weekly assessments, monthly strategic reviews, and real-time alerts calibrated to severity and recipient role.", icon: <Activity className="size-5" />, accent: "emerald" },
            { title: "5. Adaptation", body: "As the threat environment evolves, the architecture adapts. CLAIRVOYANCE CX recalibrates monitoring. LITHVIK N1 adjusts coordination. PERCEPTION X2 optimizes deployment based on real-time performance.", icon: <Compass className="size-5" />, accent: "purple" },
            { title: "6. Sovereign Standard", body: "Zero compromise security, absolute discretion, verified outcomes, integrated architecture, operational continuity. The baseline standard — not a premium tier.", icon: <ShieldCheck className="size-5" />, accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-amber-gold/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-amber-gold mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage Dr. Jyoti Kush.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Executive office engagements begin with the same discipline that has served sovereign institutions across eighteen countries. National security level confidentiality from first contact. Sovereign-grade architecture. The standard is absolute.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/sectors/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Compass className="size-4" />
                All Sectors
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Governments", description: "Governance transformation at sovereign scale.", href: "/sectors/governments/", icon: Building2, accent: "red" },
          { title: "Royal Families", description: "Absolute discretion for sovereign households.", href: "/sectors/kingdoms-royal-families/", icon: Crown, accent: "purple" },
          { title: "Political Organizations", description: "Voter intelligence and campaign engineering.", href: "/sectors/political-organizations/", icon: Vote, accent: "cyan" },
          { title: "Sectors Hub", description: "Eight sectors. One operating system.", href: "/sectors/", icon: ScrollText, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
